import React, { useCallback, useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, TextInput, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { Typography } from '../../../components/ui/Typography';
import { useTheme } from '../../../context/ThemeContext';
import { CoverCropModal } from '../../live-session/create/CoverCropModal';
import { api, getFileUrl } from '../../../services/api';
import { friendlyApiError } from '../../../services/errorMessages';
import { TITLE_MIN, isTitleValid, type RecordingEditable } from './recordingEdit';

import { ACCENT_TEXT } from '../../../styles/accentText';
/**
 * CORE-1133 (возврат QA 18.09) — правка записи из архива.
 *
 * Форма узкая намеренно: название, описание, обложка — ровно то, что просит
 * задача. Времени, длительности и видимости здесь нет: запись уже состоялась,
 * и менять её расписание нечем и незачем. Этим она и отличается от
 * `RoomEditModal`, который правит ещё не начавшуюся сессию.
 *
 * CORE-1526 (п. 1) — форма одна на архив и на страницы записи: до этого
 * править запись можно было только из архива, а со страницы самой записи —
 * никак, и мастер шёл искать тот же объект в другом разделе.
 */
export const RecordingEditModal: React.FC<{
    item: RecordingEditable;
    busy: boolean;
    onCancel: () => void;
    onSave: (draft: { title: string; description: string; coverImageUrl: string | null; coverFile: File | null }) => void;
}> = ({ item, busy, onCancel, onSave }) => {
    const { colors } = useTheme();
    const [title, setTitle] = useState(item.title ?? '');
    const [description, setDescription] = useState(item.description ?? '');
    const [cover, setCover] = useState<string | null>(item.coverImageUrl);
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [pendingCover, setPendingCover] = useState<File | null>(null);
    const [coverError, setCoverError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement | null>(null);

    const room = item.source === 'room';

    const pick = useCallback((file: File) => {
        if (!file.type.startsWith('image/')) { setCoverError('Только изображения'); return; }
        if (file.size > 10 * 1024 * 1024) { setCoverError('Файл больше 10 МБ'); return; }
        setCoverError(null);
        setPendingCover(file);
    }, []);

    /**
     * У комнаты обложка отдаётся ссылкой, поэтому файл поднимается сразу и
     * форма показывает результат. У эфира она уходит файлом в ту же форму, что
     * и остальные поля, — поднимать заранее нечем и не нужно.
     */
    const applyCover = useCallback(async (file: File) => {
        setPendingCover(null);
        if (!room) {
            setCoverFile(file);
            setCover(URL.createObjectURL(file));
            return;
        }
        setUploading(true);
        try {
            const { url } = await api.uploadRoomCover(file);
            setCover(url);
            setCoverError(null);
        } catch (e: any) {
            setCoverError(friendlyApiError(e));
        } finally {
            setUploading(false);
        }
    }, [room]);

    const titleOk = isTitleValid(title);
    const preview = cover ? getFileUrl(cover) : null;

    return (
        <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
            {/* CORE-1133 (возврат QA 29.09) — форма обязана отделять
                редактируемое от страницы под ней. Подложка берётся
                непрозрачным токеном: остальные surface-токены на вебе
                намеренно полупрозрачны, и сквозь них читались название
                фоновой карточки, фильтры, счётчики и граница футера.
                Затемнение 0.55 — то же, что у соседних модалок. */}
            <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
                <View style={{
                          width: '100%', maxWidth: 620, maxHeight: '90%',
                          backgroundColor: colors.surfaceDialog,
                          borderWidth: 1, borderColor: colors.border,
                          borderRadius: 16, padding: 20,
                      }}
                      testID="recording-edit-modal">
                    <Typography variant="h3" style={{ color: colors.textPrimary, marginBottom: 12 }}>
                        Редактировать запись
                    </Typography>

                    <ScrollView>
                        <Typography variant="caption" style={{ color: colors.textMuted, marginBottom: 4 }}>Название</Typography>
                        <TextInput
                            testID="recording-edit-title"
                            value={title}
                            onChangeText={setTitle}
                            placeholder="Название записи"
                            placeholderTextColor={colors.textMuted}
                            style={{ color: colors.textPrimary, backgroundColor: colors.canvas, borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 10, marginBottom: 4 }}
                        />
                        {!titleOk && (
                            <Typography variant="caption" style={{ color: '#C9553D', marginBottom: 8 }}>
                                {`Не короче ${TITLE_MIN} символов`}
                            </Typography>
                        )}

                        <Typography variant="caption" style={{ color: colors.textMuted, marginTop: 8, marginBottom: 4 }}>Описание</Typography>
                        <TextInput
                            testID="recording-edit-description"
                            value={description}
                            onChangeText={setDescription}
                            placeholder="О чём эта запись"
                            placeholderTextColor={colors.textMuted}
                            multiline
                            numberOfLines={4}
                            style={{ color: colors.textPrimary, backgroundColor: colors.canvas, borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 10, minHeight: 90, textAlignVertical: 'top' }}
                        />

                        <Typography variant="caption" style={{ color: colors.textMuted, marginTop: 12, marginBottom: 6 }}>Обложка</Typography>
                        <Pressable
                            testID="recording-edit-cover"
                            onPress={() => fileRef.current?.click()}
                            style={{ backgroundColor: colors.canvas, borderWidth: 1, borderColor: colors.border, borderRadius: 10, height: 120, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                        >
                            {uploading ? <ActivityIndicator color={colors.textMuted} />
                                : preview ? <Image source={{ uri: preview }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                                : <Ionicons name="image-outline" size={24} color={colors.textMuted} />}
                        </Pressable>
                        {coverError && (
                            <Typography variant="caption" style={{ color: '#C9553D', marginTop: 6 }}>{coverError}</Typography>
                        )}
                        {/* Скрытый выбор файла — на вебе это единственный способ
                            открыть диалог. CORE-1526 (ревью) — за веткой платформы
                            (INVARIANTS I-1): форма теперь открывается и из общего
                            экрана эфира, а на планшете `input` уронил бы его. */}
                        {Platform.OS === 'web' && React.createElement('input', {
                            ref: fileRef,
                            type: 'file',
                            accept: 'image/*',
                            style: { display: 'none' },
                            onChange: (e: any) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; },
                        })}
                    </ScrollView>

                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
                        <Pressable onPress={onCancel} testID="recording-edit-cancel" accessibilityRole="button">
                            <Typography variant="body" style={{ color: colors.textMuted }}>Отмена</Typography>
                        </Pressable>
                        <Pressable
                            testID="recording-edit-save"
                            accessibilityRole="button"
                            disabled={!titleOk || busy || uploading}
                            onPress={() => onSave({ title, description, coverImageUrl: cover, coverFile })}
                        >
                            <Typography variant="body" style={{ color: (!titleOk || busy || uploading) ? colors.textMuted : ACCENT_TEXT, fontWeight: '700' }}>
                                {busy ? 'Сохраняем…' : 'Сохранить'}
                            </Typography>
                        </Pressable>
                    </View>
                </View>
            </View>

            {pendingCover && (
                <CoverCropModal file={pendingCover} onCancel={() => setPendingCover(null)} onApply={(f) => { void applyCover(f); }} />
            )}
        </Modal>
    );
};
